import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSuiteRunsTableRowActions } from '@/features/executions/components/useSuiteRunsTableRowActions';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { querySuiteRuns } from '@/features/executions/data';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  buildSuiteDetailPath,
  buildSuiteRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import {
  TableV2MetricCells,
  TableV2MetricHeaders,
} from '@/features/shared/components/tableSystem/TableV2MetricColumns';
import {
  queryAllTableV2NestedRows,
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NestedCollapse,
  TableV2NameCell,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2SortableHeader,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2BodyRowHeightPx,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
  tableV2NestedBodyRowHeightPx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import { TableV2SkeletonRow } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { buildTagColorByName } from '@/features/shared/tags';
import { DeleteSuiteDialog } from '@/features/suites/components/dialogs';
import {
  countSuiteRunTests,
  defaultNestedSuiteRunSort,
  defaultSuiteV2Sort,
  resolveSuiteRunChildCount,
} from '@/features/suites/components/SuitesTableV2.helpers';
import { useSuitesTableRowActions } from '@/features/suites/components/useSuitesTableRowActions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import { s } from '@/theme/tokens';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import { SuiteRunExpandedTests } from './SuiteRunExpandedTests';
import { buildSuiteRunHeader, type SortableNestedSuiteRunField } from './SuitesTableV2Headers';
import {
  buildSuiteColgroup,
  buildSuiteSkeletonColumns,
  suiteRunColgroup,
  suiteRunSkeletonColumns,
} from './SuitesTableV2Layout';
import type { SortableSuiteField, SuitesTableV2Props } from './SuitesTableV2.types';
import type { TagColor } from '@/shared/data/tags';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  QueryResult,
  SuiteListItemDto,
  SuiteRunDto,
  TestRunDto,
} from '@flowqa/shared';

const SuiteRunNestedRow = ({
  actionsDisabled,
  deleteRowPendingId,
  isLastVisibleRow,
  linksDisabled,
  row,
  sortingDisabled,
  tagColorByName,
  testRunsQueryFn,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteTestRunRow,
  onUpdateRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  linksDisabled: boolean;
  row: SuiteRunDto;
  sortingDisabled: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: SuiteRunDto; tags: string[]; colors: TagColor[] }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const name = row.displayName ?? row.suiteName ?? row.name ?? row.id;
  const suiteRunNumberLabel = formatTableV2RunNumber(row.runNumber) ?? '-';
  const suiteRunHref = buildSuiteRunPath(row.suiteSlug ?? row.suiteId, row.runNumber);
  const testCount = countSuiteRunTests(row).total;

  return (
    <>
      <TableRow
        sx={[
          tableV2NestedDepthSx[1].bodyRow,
          {
            '& > td': {
              borderBottom:
                expanded || isLastVisibleRow
                  ? 0
                  : (theme) => `1px solid ${theme.app.colors.border}`,
              height: `${String(tableV2NestedBodyRowHeightPx)}px`,
              py: s('sm'),
              verticalAlign: 'middle',
            },
          },
        ]}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse test runs' : 'Expand test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={linksDisabled ? null : suiteRunHref}
            primary={suiteRunNumberLabel}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell counts={countSuiteRunTests(row)} />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={row.durationMs} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.timestamp} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          {actionsDisabled ? null : (
            <TableActionsCell
              rowName={name}
              verticalAlign="center"
              deleteActionLabel={getEntityActionLabel('delete', 'Suite Run')}
              editTagsEntityNoun="suite run"
              rowTags={row.tags ?? []}
              onUpdateTagsColor={(input) => {
                onUpdateRowTagsColor({ row, ...input });
              }}
              updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
              onDelete={() => {
                onDeleteRow(row);
              }}
              disableDelete={deleteRowPendingId === row.id}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={8}>
          <SuiteRunExpandedTests
            actionsDisabled={actionsDisabled}
            childCount={testCount}
            deleteRowPendingId={testRunDeleteRowPendingId}
            expanded={expanded}
            linksDisabled={linksDisabled}
            organisationId={row.organisationId}
            projectId={row.projectId}
            sortingDisabled={sortingDisabled}
            tagColorByName={tagColorByName}
            suiteRunId={row.id}
            testRunsQueryFn={testRunsQueryFn}
            updateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

const SuiteExpandedRuns = ({
  actionsDisabled,
  childCount,
  deleteRowPendingId,
  expanded,
  linksDisabled,
  organisationId,
  projectId,
  suiteId,
  suiteRunsQueryFn,
  sortingDisabled,
  tagColorByName,
  testRunsQueryFn,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  updateRowTagsColorPendingId,
  onDeleteSuiteRunRow,
  onDeleteTestRunRow,
  onUpdateSuiteRunRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  childCount: number;
  deleteRowPendingId: string | null;
  expanded: boolean;
  linksDisabled: boolean;
  organisationId: string | null;
  projectId: string;
  suiteId: string;
  suiteRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  sortingDisabled: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteSuiteRunRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onUpdateSuiteRunRowTagsColor: (input: {
    row: SuiteRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultNestedSuiteRunSort);
  const handleSortChange = (field: SortableNestedSuiteRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const header = buildSuiteRunHeader({
    sort,
    sortingDisabled,
    onSortChange: handleSortChange,
  });
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.suiteRuns,
      'v2-nested',
      organisationId,
      projectId,
      suiteId,
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () => {
      const input = {
        page: 1,
        pageSize: 1,
        sorts: [sort],
        filters: buildEqFilter('suiteId', suiteId),
      };
      return await queryAllTableV2NestedRows(
        input,
        suiteRunsQueryFn ??
          (async (nextInput) => await querySuiteRuns(projectId, organisationId, nextInput)),
      );
    },
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <Box sx={tableV2NestedDepthSx[1].frame}>
            <Table aria-label="Suite runs loading" sx={tableV2NestedDepthSx[1].table}>
              {suiteRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`suite-runs-nested-skeleton-${String(index)}`}
                    columns={suiteRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : query.data && query.data.items.length > 0 ? (
          <Box sx={tableV2NestedDepthSx[1].frame}>
            <Table aria-label="Suite runs" sx={tableV2NestedDepthSx[1].table}>
              {suiteRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((suiteRun, index, suiteRuns) => (
                  <SuiteRunNestedRow
                    key={suiteRun.id}
                    actionsDisabled={actionsDisabled}
                    deleteRowPendingId={deleteRowPendingId}
                    isLastVisibleRow={index === suiteRuns.length - 1}
                    linksDisabled={linksDisabled}
                    row={suiteRun}
                    sortingDisabled={sortingDisabled}
                    tagColorByName={tagColorByName}
                    testRunsQueryFn={testRunsQueryFn}
                    testRunDeleteRowPendingId={testRunDeleteRowPendingId}
                    testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
                    updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                    onDeleteRow={onDeleteSuiteRunRow}
                    onDeleteTestRunRow={onDeleteTestRunRow}
                    onUpdateRowTagsColor={onUpdateSuiteRunRowTagsColor}
                    onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Text role="body" color="textSecondary">
            No data
          </Text>
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};

const SuiteRow = ({
  actionsDisabled,
  deleteRowPendingId,
  isLastVisibleRow,
  linksDisabled,
  row,
  showProjectName,
  sortingDisabled,
  suiteRunsQueryFn,
  suiteRunDeleteRowPendingId,
  suiteRunUpdateRowTagsColorPendingId,
  tagColorByName,
  testRunsQueryFn,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  thresholds,
  visibleMetricKeys,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteSuiteRunRow,
  onDeleteTestRunRow,
  onUpdateRowTagsColor,
  onUpdateSuiteRunRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  linksDisabled: boolean;
  row: SuiteListItemDto;
  showProjectName: boolean;
  sortingDisabled: boolean;
  suiteRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  suiteRunDeleteRowPendingId: string | null;
  suiteRunUpdateRowTagsColorPendingId: string | null;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  thresholds: MetricThresholdGroup;
  visibleMetricKeys: MetricThresholdGroupKey[];
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: SuiteListItemDto) => void;
  onDeleteSuiteRunRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: {
    row: SuiteListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateSuiteRunRowTagsColor: (input: {
    row: SuiteRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const latestRun = row.latestRun ?? null;
  const metrics = row.metrics ?? null;
  const suiteName = row.displayName ?? row.name;
  const suiteRunCount = resolveSuiteRunChildCount(row);

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: 'background.paper',
          '& > td': {
            borderBottom:
              expanded || isLastVisibleRow ? 0 : (theme) => `1px solid ${theme.app.colors.border}`,
            height: `${String(tableV2BodyRowHeightPx)}px`,
            py: s('md'),
            verticalAlign: 'middle',
          },
        }}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse suite runs' : 'Expand suite runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={linksDisabled ? null : buildSuiteDetailPath(row.slug)}
            primary={suiteName}
            secondary={showProjectName ? (row.project?.name ?? null) : null}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={latestRun?.status ?? null} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={latestRun?.timestamp ?? null} />
        </TableCell>
        <TableV2MetricCells
          metrics={metrics}
          thresholds={thresholds}
          visibleMetricKeys={visibleMetricKeys}
        />
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          {actionsDisabled ? null : (
            <TableActionsCell
              rowName={suiteName}
              verticalAlign="center"
              deleteActionLabel={getEntityActionLabel('delete', 'Suite')}
              editTagsEntityNoun="suite"
              rowTags={row.tags ?? []}
              onUpdateTagsColor={(input) => {
                onUpdateRowTagsColor({ row, ...input });
              }}
              updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
              onDelete={() => {
                onDeleteRow(row);
              }}
              disableDelete={deleteRowPendingId === row.id}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={buildSuiteSkeletonColumns(visibleMetricKeys).length}>
          <SuiteExpandedRuns
            actionsDisabled={actionsDisabled}
            childCount={suiteRunCount}
            deleteRowPendingId={suiteRunDeleteRowPendingId}
            expanded={expanded}
            linksDisabled={linksDisabled}
            organisationId={row.organisationId}
            projectId={row.projectId}
            suiteId={row.id}
            suiteRunsQueryFn={suiteRunsQueryFn}
            sortingDisabled={sortingDisabled}
            tagColorByName={tagColorByName}
            testRunsQueryFn={testRunsQueryFn}
            testRunDeleteRowPendingId={testRunDeleteRowPendingId}
            testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            updateRowTagsColorPendingId={suiteRunUpdateRowTagsColorPendingId}
            onDeleteSuiteRunRow={onDeleteSuiteRunRow}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onUpdateSuiteRunRowTagsColor={onUpdateSuiteRunRowTagsColor}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const SuitesTableV2 = ({
  actionsDisabled = false,
  active,
  fixedFilters,
  forceLoading = false,
  linksDisabled = false,
  noRowsText = 'No data',
  onQueryMeta,
  queryFn,
  queryKey,
  requireAuth = true,
  search,
  selectedProjectId = null,
  showProjectName = true,
  sortingDisabled = false,
  suiteRunsQueryFn,
  tagOptionsOverride = [],
  testRunsQueryFn,
}: SuitesTableV2Props): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultSuiteV2Sort);
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const visibleMetricKeys = resolveVisibleMetricThresholdKeys(thresholds.suites);
  const skeletonColumns = buildSuiteSkeletonColumns(visibleMetricKeys);
  const tagColorByName = buildTagColorByName(tagOptionsOverride);
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useSuitesTableRowActions({ selectedProjectId });
  const {
    deleteLoading: suiteRunDeleteLoading,
    deleteRowPendingId: suiteRunDeleteRowPendingId,
    deleteTarget: suiteRunDeleteTarget,
    onDeleteClose: onSuiteRunDeleteClose,
    onDeleteConfirm: onSuiteRunDeleteConfirm,
    onDeleteRow: onDeleteSuiteRunRow,
    onUpdateRowTagsColor: onUpdateSuiteRunRowTagsColor,
    updateRowTagsColorPendingId: suiteRunUpdateRowTagsColorPendingId,
  } = useSuiteRunsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: testRunDeleteLoading,
    deleteRowPendingId: testRunDeleteRowPendingId,
    deleteTarget: testRunDeleteTarget,
    onDeleteClose: onTestRunDeleteClose,
    onDeleteConfirm: onTestRunDeleteConfirm,
    onDeleteRow: onDeleteTestRunRow,
    onUpdateRowTagsColor: onUpdateTestRunRowTagsColor,
    updateRowTagsColorPendingId: testRunUpdateRowTagsColorPendingId,
  } = useTestRunsTableRowActions({ selectedProjectId });

  const handleSortChange = (field: SortableSuiteField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          disabled={sortingDisabled}
          field="name"
          label="Suite"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2HeaderLabel label="Latest Status" />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          disabled={sortingDisabled}
          field="latestRunTimestamp"
          label="Ran At"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableV2MetricHeaders
        sort={sort}
        sortingDisabled={sortingDisabled}
        visibleMetricKeys={visibleMetricKeys}
        onSortChange={handleSortChange}
      />
      <TableCell>
        <TableV2HeaderLabel label="Tags" />
      </TableCell>
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Suites"
        colgroup={buildSuiteColgroup(visibleMetricKeys)}
        emptyText={noRowsText}
        entityLabel="suites"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={header}
        queryFn={queryFn}
        queryKey={queryKey}
        requireAuth={requireAuth}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={[sort]}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <SuiteRow
            key={row.id}
            actionsDisabled={actionsDisabled}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            linksDisabled={linksDisabled}
            row={row}
            showProjectName={showProjectName}
            sortingDisabled={sortingDisabled}
            suiteRunDeleteRowPendingId={suiteRunDeleteRowPendingId}
            suiteRunUpdateRowTagsColorPendingId={suiteRunUpdateRowTagsColorPendingId}
            suiteRunsQueryFn={suiteRunsQueryFn}
            tagColorByName={tagColorByName}
            testRunDeleteRowPendingId={testRunDeleteRowPendingId}
            testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            testRunsQueryFn={testRunsQueryFn}
            thresholds={thresholds.suites}
            visibleMetricKeys={visibleMetricKeys}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onDeleteSuiteRunRow={onDeleteSuiteRunRow}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
            onUpdateSuiteRunRowTagsColor={onUpdateSuiteRunRowTagsColor}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        )}
      />
      <DeleteSuiteDialog
        open={deleteTarget !== null}
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={suiteRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'suiteRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'suiteRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'suiteRun',
        })}
        confirmColor="error"
        loading={suiteRunDeleteLoading}
        onClose={onSuiteRunDeleteClose}
        onConfirm={onSuiteRunDeleteConfirm}
      />
      <ConfirmDialog
        open={testRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={testRunDeleteLoading}
        onClose={onTestRunDeleteClose}
        onConfirm={onTestRunDeleteConfirm}
      />
    </>
  );
};
